import React, {ChangeEvent, ReactElement} from "react";

import {BaseInput} from "/src/types";

import {css} from "/src/utils/utils";

import Label from "../label/Label";
import ErrorMessage from "../error-message/ErrorMessage";
import Icon, {IconSVG} from "../../icon/Icon";
import CheckIcon from "/src/svg/check.svg";

import * as styles from "./checkbox.module.scss";

export interface CheckboxProps extends BaseInput {
  onChange?: (value: string, checked: boolean, name?: string) => unknown;
  checked?: boolean;
  isPill?: boolean;
  fluid?: boolean;
  classes?: string;
  iconClasses?: string;
  selectedClasses?: string;
  icon?: IconSVG;
}

const Checkbox = (props: CheckboxProps): ReactElement => {
  const {
    label = "",
    required = false,
    name = "",
    errorMessage = "",
    status,
    value,
    checked = false,
    disabled = false,
    readOnly = false,
    onChange,
    darkMode = false,
    isPill = false,
    fluid = false,
    classes,
    iconClasses,
    selectedClasses,
    icon,
  } = props;

  // due to SSR, we shouldn't set initial state from props
  // this will cause component style not being rendered properly if there is
  // checked prop on production build
  // instead, we should use useEffect to update the state value
  const [isChecked, setIsChecked] = React.useState<boolean>(false);
  const [isFocusedState, setIsFocusedState] = React.useState<boolean>(false);

  const isInvalid = status === "error";
  const isValid = status === "success";

  React.useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  const onInputFocus = () => {
    // if status is not error/success, set focus state
    if (!isInvalid && !isValid) {
      setIsFocusedState(true);
    }
  };

  const onInputBlur = () => {
    setIsFocusedState(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onInputChange = (_e: ChangeEvent<HTMLInputElement>) => {
    const tempIsChecked = !isChecked;
    setIsChecked(tempIsChecked);

    const tempValue = value != null ? value : name;
    // if it's a native checkbox, and it's not selected, we should return empty value on change
    let checkboxValueOnChange = tempValue;
    if (!isPill && !tempIsChecked) {
      checkboxValueOnChange = "";

      // clear focus so it's easier to tell the pill has been unselected
      setIsFocusedState(false);
    }
    onChange && onChange(checkboxValueOnChange, tempIsChecked, name);
  };

  return (
    <label
      data-testid="checkbox"
      className={css(
        styles.checkbox,
        fluid ? styles.checkboxFluid : "",
        darkMode ? styles.checkboxDark : "",
        isFocusedState ? styles.checkboxFocus : "",
        isInvalid ? styles.checkboxError : "",
        isValid ? styles.checkboxSuccess : "",
        isChecked ? styles.checkboxChecked : "",
        disabled ? styles.checkboxDisabled : "",
        classes!
      )}
    >
      {!isPill ? (
        <div className={css(styles.checkbox__elWrapper)}>
          <div className={css(styles.checkbox__el)}>
            <input
              type="checkbox"
              name={name}
              value={value === null ? undefined : value}
              disabled={disabled}
              readOnly={readOnly}
              defaultChecked={checked}
              required={required}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              onChange={onInputChange}
              className={css(styles.checkbox__el__input)}
            />
            <div className={css(styles.checkbox__el__bg)}>
              <Icon
                svg={CheckIcon}
                aria-hidden="true"
                role="presentation"
                classes={css(styles.checkbox__el__icon, iconClasses!)}
              />
            </div>
          </div>
          <Label
            label={label}
            required={required}
            darkMode={darkMode}
            disabled={disabled}
          />
        </div>
      ) : (
        <div
          className={css(
            styles.checkbox__el,
            styles.checkbox__elPill,
            isChecked ? selectedClasses! : ""
          )}
        >
          {icon != null && (
            <Icon
              aria-hidden="true"
              role="presentation"
              classes={css(styles.checkbox__el__iconPill, iconClasses!)}
              svg={icon}
            />
          )}
          <div>
            <input
              type="checkbox"
              name={name}
              value={value === null ? undefined : value}
              disabled={disabled}
              readOnly={readOnly}
              defaultChecked={checked}
              required={required}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              onChange={onInputChange}
              className={css(styles.checkbox__el__input)}
              aria-label={label}
            />
            <Label
              ariaHidden
              label={label}
              required={required}
              darkMode={darkMode}
              disabled={disabled}
              classes={styles.checkbox__el__lbl}
            />
          </div>
        </div>
      )}
      {/* not to render individual error message if isPill is true
          this is with the assumption that, isPill === true === multiselect, error message should be handled in multiselect component
      */}
      {!isPill && <ErrorMessage message={errorMessage} visible={isInvalid} />}
    </label>
  );
};

export default Checkbox;
